import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from '@material-ui/core/Button';
// Общий стиль статей: каждая берёт из него только нужные классы
// eslint-disable-next-line css-modules/no-unused-class
import s from '../../components/Article/Article.css';
import Link from '../../components/Link';
import StagedLink from '../../components/StagedLink';
import { CHROME_STORE_URL } from '../../utils/seo';

/**
 * /how-to-change-chrome-background
 *
 * Факты о New Tab Studio — только из data/screenshots/report-4-*.md
 * (раздел wallpaper отчёта по дизайн-виджетам). Про сам Chrome пишем
 * только то, что видно в его панели Customize Chrome, без чисел.
 */
function ChromeBackground() {
	return (
		<div className={s.root}>
			<p className={s.lead}>
				To change the background on Chrome, open a new tab, click{' '}
				<b>Customize Chrome</b> in the bottom right corner and pick a
				picture under <b>Change theme</b>. For a still photo that’s the
				whole job. The rest of this page covers the cases where it
				isn’t: the button has vanished, you want dark mode, or you’re
				after a GIF, a video or a wallpaper that changes by itself.
			</p>

			<nav className={s.toc} aria-label="On this page">
				<div className={s.label}>On this page</div>
				<ol>
					<li>
						<a href="#built-in">The built-in way</a>
					</li>
					<li>
						<a href="#missing">
							When there’s no Customize Chrome button
						</a>
					</li>
					<li>
						<a href="#dark-mode">Dark mode and colours</a>
					</li>
					<li>
						<a href="#moving">GIF and video backgrounds</a>
					</li>
					<li>
						<a href="#rotating">A wallpaper that changes itself</a>
					</li>
					<li>
						<a href="#questions">Questions people ask</a>
					</li>
					<li>
						<a href="#undo">Back to normal</a>
					</li>
				</ol>
			</nav>

			<h2 id="built-in">
				Changing the background with Chrome’s own settings
			</h2>

			<ol className={s.steps}>
				<li>
					Open a new tab: <b>Ctrl+T</b>, or <b>Cmd+T</b> on a Mac.
				</li>
				<li>
					Click <b>Customize Chrome</b> at the bottom right. A panel
					slides out on the right.
				</li>
				<li>
					Choose <b>Change theme</b>.
				</li>
				<li>
					Open a collection and click a picture. Or pick{' '}
					<b>Upload an image</b> and use a photo from your computer.
				</li>
				<li>Close the panel. The new background is already on.</li>
			</ol>

			<figure className={s.figure}>
				<img
					src="/img/widgets/chrome-background-customize.webp"
					srcSet="/img/widgets/chrome-background-customize-720.webp 720w, /img/widgets/chrome-background-customize.webp 1440w"
					sizes="(max-width: 800px) 100vw, 760px"
					alt="Google Chrome new tab with the Customize Chrome side panel open on Themes and its wallpaper collections"
					width={1440}
					height={900}
					loading="lazy"
				/>
				<figcaption>
					Chrome&rsquo;s own option: Customize Chrome, then Change
					theme
				</figcaption>
			</figure>

			<p>
				Inside a collection you’ll find a <b>Refresh daily</b> switch.
				It swaps the picture for another one from the same collection
				every day, and it’s the only automatic option Chrome has.
			</p>

			<div className={s.note}>
				This sets the picture on the new tab page. Websites keep their
				own backgrounds, and Chrome has no setting that puts a photo
				behind them. When people say they changed the whole browser,
				they usually mean the colour theme, which is the next step in
				the same panel.
			</div>

			<h2 id="missing">Why can’t I change my Chrome background?</h2>

			<p>
				Usually because the page in front of you isn’t Chrome’s. The
				Customize Chrome button lives on Google’s own new tab, so
				anything that replaces that page takes the button with it. Go
				through these in order:
			</p>

			<ul>
				<li>
					<b>An extension has taken over the new tab.</b> Then the
					background is set inside that extension, not in Chrome. If
					you don’t want it any more, switch it off at{' '}
					<b>chrome://extensions</b> and Chrome’s page comes back with
					the button.
				</li>
				<li>
					<b>Google isn’t your default search engine.</b> Chrome only
					shows its customisable page when searches go to Google.
					Check <b>Settings → Search engine</b>.
				</li>
				<li>
					<b>A work or school computer.</b> If the Chrome menu says
					it’s managed by your organisation, the administrator may
					have locked the look of the browser. Nothing to fix on your
					side there.
				</li>
				<li>
					<b>An Incognito or Guest window.</b> These always open the
					plain page, whatever you picked in your normal profile.
				</li>
			</ul>

			<h2 id="dark-mode">Dark mode and colours</h2>

			<p>
				The same panel handles colour. Under the theme you can pick a
				colour for the browser frame and switch between <b>Light</b>,{' '}
				<b>Dark</b> and <b>Device</b>, which follows your system
				setting. Dark mode darkens the tabs, the menus and the page
				around your picture. It doesn’t touch the picture itself, so a
				bright beach photo stays a bright beach photo.
			</p>

			<p>
				If you want no photo at all, a flat colour works too. Some
				people find it easier on the eyes than any wallpaper, and it
				loads instantly.
			</p>

			<h2 id="moving">GIF and video backgrounds (live wallpaper)</h2>

			<p>
				Chrome’s panel is made for still pictures. For something that
				moves you’ll need an extension, and they come in two kinds.
			</p>

			<p>
				<b>One scene per extension.</b> Search the Chrome Web Store for
				live wallpaper and you’ll get pages of listings, each shipping a
				single animated scene: a cabin in the snow, a black hole, a game
				character. If you love that exact scene, fine. When you get
				bored of it, though, the only way to change it is to install a
				different extension.
			</p>

			<p>
				<b>Extensions that take any source.</b> This is where New Tab
				Studio sits, and since it’s ours, weigh this paragraph
				accordingly. Its background picker has a <b>GIF search</b> tab
				for animated backgrounds, and the <b>Image URL</b> tab accepts a
				direct link to a video file (MP4 or WebM, plus a few rarer
				formats). The video plays silently on a loop.
			</p>

			<figure className={s.figure}>
				<img
					src="/img/widgets/chrome-background-gif.webp"
					srcSet="/img/widgets/chrome-background-gif-720.webp 720w, /img/widgets/chrome-background-gif.webp 1440w"
					sizes="(max-width: 800px) 100vw, 760px"
					alt="Background picker on the GIF search tab showing animated results for rain drops"
					width={1440}
					height={900}
					loading="lazy"
				/>
				<figcaption>
					Search GIPHY for a looping background, rain drops included
				</figcaption>
			</figure>

			<figure className={s.figure}>
				<img
					src="/img/widgets/chrome-background-video.webp"
					srcSet="/img/widgets/chrome-background-video-720.webp 720w, /img/widgets/chrome-background-video.webp 1440w"
					sizes="(max-width: 800px) 100vw, 760px"
					alt="Background picker with an mp4 link pasted into the URL field, over a new tab whose video background shows drifting clouds"
					width={1440}
					height={900}
					loading="lazy"
				/>
				<figcaption>
					Paste an mp4 link and the clouds start moving behind your
					widgets
				</figcaption>
			</figure>

			<p>
				Two limits worth knowing before you go hunting for clips. A
				video background doesn’t rotate on a timer the way photos can.
				And you can’t upload a video from your computer, only link to
				one; uploads are for images (JPG, PNG, WEBP or GIF, up to 10
				MB). Also, a clip behind every tab means a clip plays every time
				you open one. On an older laptop, keep it short.
			</p>

			<h2 id="rotating">A wallpaper that changes by itself</h2>

			<p>
				Refresh daily picks from Google’s collections, once a day. If
				you’d rather choose the source and the pace yourself, that’s the
				other thing extensions are for. In New Tab Studio, the{' '}
				<StagedLink to="/widgets/wallpaper">
					background picker
				</StagedLink>{' '}
				has eight tabs. Two of them can keep the picture changing for
				you:
			</p>

			<ul>
				<li>
					<b>Collections</b>: curated sets, including a holiday group
					(Christmas, Halloween, Thanksgiving and so on).
				</li>
				<li>
					<b>Wallpapers</b>: pulled from wallhaven.cc across 20
					categories, from Mountains and Space to Anime and Japan. It
					only returns safe-for-work, landscape images of at least
					1920×1080, and there’s a resolution filter. Pick{' '}
					<b>My screen and up</b> and it counts the physical pixels of
					your display, so a 4K monitor won’t get Full HD.{' '}
					<b>Shuffle</b> sets a random one from the category and keeps
					going.
				</li>
			</ul>

			<p>
				Then set the pace: every 5 minutes, every hour, every 6 hours
				(the default), every 24 hours, every week, or a custom number of
				minutes. The pin in the bottom bar stops the rotation on a
				picture you like, and <b>Show previous image</b> steps back
				through the last ten, which is handy when the shuffle takes away
				a good one.
			</p>

			<p>
				If you use{' '}
				<StagedLink to="/widgets/workspaces">workspaces</StagedLink>,
				each one can have its own background. Switch on{' '}
				<b>Only for workspace</b> in the picker. That background stays
				put: automatic changes only apply to the shared one.
			</p>

			<h3>Your own photo</h3>

			<p>
				Chrome’s Upload an image does this well enough for one picture.
				In New Tab Studio the <b>Upload</b> tab takes JPG, PNG, WEBP and
				GIF files up to 10 MB, and the file stays in your browser rather
				than going to a server. <b>Favorite images</b> keeps the ones
				you’ve liked, so you can come back to them. The{' '}
				<b>Solid color</b> tab has 16 ready colours, a full palette and
				a HEX field for the exact shade. No gradients, if that’s what
				you were hoping for.
			</p>

			<div className={s.cta}>
				<p>
					New Tab Studio is free to install and needs no account. It
					also puts widgets on top of the wallpaper, which is the part
					Chrome can’t do at all.
				</p>
				<Button
					href={CHROME_STORE_URL}
					color="primary"
					variant="contained"
				>
					Add New Tab Studio to Chrome
				</Button>
			</div>

			<h2 id="questions">
				Questions people ask about Chrome backgrounds
			</h2>

			<h3>Can I put a GIF as my Chrome background?</h3>
			<p>
				Chrome’s own Customize panel is made for still pictures, so the
				reliable route to a moving background is a new tab extension. In
				New Tab Studio you open the background picker, find one in{' '}
				<b>GIF search</b> or upload a GIF of your own, and it loops
				behind your widgets.
			</p>

			<h3>How do I turn off the dark background on Chrome?</h3>
			<p>
				Open <b>Customize Chrome</b> and switch the mode from Dark to{' '}
				<b>Light</b>. If it’s set to Device, Chrome follows your system,
				so a dark Windows or macOS theme keeps it dark; either change
				the system setting or pick Light in Chrome to override it.
			</p>

			<h3>Does a video or GIF background slow Chrome down?</h3>
			<p>
				A little, since the clip plays whenever a new tab is open, so a
				short, small file is kinder to an older laptop. From version
				4.0.1 New Tab Studio pauses a video background in tabs you’re
				not looking at, and a video never rotates on a timer.
			</p>

			<h2 id="undo">Changing your Chrome background back to normal</h2>

			<p>
				In Chrome: <b>Customize Chrome → Change theme</b>, then{' '}
				<b>Classic Chrome</b>. That removes the picture and puts back
				the plain page.
			</p>

			<p>
				If an extension set the background, removing the extension is
				the whole undo. Open <b>chrome://extensions</b>, click{' '}
				<b>Remove</b>, and your next new tab is Chrome’s page with
				whatever background you had there before. Bookmarks and history
				aren’t affected either way.
			</p>

			<p>
				Want to change more than the picture? There’s a separate guide
				to{' '}
				<StagedLink to="/how-to-change-new-tab">
					changing the new tab page in Chrome
				</StagedLink>{' '}
				altogether, and a gallery of{' '}
				<Link to="/themes">ready-made pages other people built</Link>,
				wallpaper included.
			</p>
		</div>
	);
}

export default withStyles(s)(ChromeBackground);
